/**
 * @fileoverview Kanban task card (MOR-696 Stage 2)
 *
 * A single draggable card on the task board. Surfaces the task title plus
 * the at-a-glance metadata practitioners sort on: due date + overdue flag,
 * priority, assignee avatar, labels, and subtask progress. Colour comes from
 * the same `colorForEvent` precedence the calendar uses so the two surfaces
 * stay visually consistent.
 *
 * Drag behaviour is provided by `@dnd-kit/sortable`; the parent board owns
 * the `DndContext` and persistence.
 */

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { format, parseISO } from "date-fns";
import { Ban, Briefcase, CalendarClock, Repeat } from "lucide-react";

import { type CalendarEventRow, isEventKind } from "@/calendar/eventAdapter";
import {
  AutomationChip,
  type RowAutomation,
} from "@/calendar/components/automation-chip";
import { typeBadgeMeta } from "@/calendar/categoryMeta";
import { priorityMeta } from "@/calendar/task-priority";
import { TASK_COLOR_DOT } from "@/components/settings/tasks/task-colors";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { UserAvatar } from "@/ui/composed";
import { cn } from "@/utils/misc";

export interface BoardMember {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  avatarUrl?: string;
}

function shortDate(iso: string): string {
  try {
    return format(parseISO(iso), "d MMM");
  } catch {
    return iso;
  }
}

export function TaskBoardCard({
  event,
  member,
  labelLookup,
  showJobName = false,
  automations,
  onClick,
}: {
  event: CalendarEventRow;
  member: BoardMember | null;
  labelLookup: Map<string, { name: string; color: BadgeColor }>;
  /** Show the job name at the bottom of the card (cross-job workspace view). */
  showJobName?: boolean;
  /** Automation rules targeting this task (⚡ indicator). */
  automations?: RowAutomation[];
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(event.id), data: { event } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      onClick={onClick}
      {...attributes}
      {...listeners}
      className={cn(
        "w-full cursor-grab select-none rounded-md border border-border bg-background p-2.5 text-left text-sm shadow-sm active:cursor-grabbing",
        "hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        // While dragging, the real card collapses into a faint placeholder gap
        // and the lifted copy is rendered in the board's DragOverlay instead.
        isDragging && "opacity-40",
      )}
    >
      <TaskBoardCardBody
        event={event}
        member={member}
        labelLookup={labelLookup}
        showJobName={showJobName}
        automations={automations}
      />
    </button>
  );
}

/**
 * Lifted copy of a card rendered inside the board's `DragOverlay` so the card
 * visibly follows the cursor while dragging (instead of just dimming in place).
 */
export function TaskBoardCardPreview({
  event,
  member,
  labelLookup,
  showJobName = false,
  automations,
}: {
  event: CalendarEventRow;
  member: BoardMember | null;
  labelLookup: Map<string, { name: string; color: BadgeColor }>;
  showJobName?: boolean;
  automations?: RowAutomation[];
}) {
  return (
    <div className="w-72 max-w-full rotate-2 cursor-grabbing select-none rounded-md border border-foreground/20 bg-background p-2.5 text-left text-sm shadow-2xl ring-2 ring-primary/40">
      <TaskBoardCardBody
        event={event}
        member={member}
        labelLookup={labelLookup}
        showJobName={showJobName}
        automations={automations}
      />
    </div>
  );
}

/** Shared visual content for both the sortable card and the drag overlay. */
function TaskBoardCardBody({
  event,
  member,
  labelLookup,
  showJobName = false,
  automations,
}: {
  event: CalendarEventRow;
  member: BoardMember | null;
  labelLookup: Map<string, { name: string; color: BadgeColor }>;
  showJobName?: boolean;
  automations?: RowAutomation[];
}) {
  const priority = priorityMeta(event.priority);
  const showPriority = !!event.priority && event.priority > 0;
  const categoryMeta = typeBadgeMeta(
    isEventKind(event),
    event.category,
    Boolean(event.parentTaskId),
  );
  const CategoryIcon = categoryMeta.icon;
  const labels = event.labelIds
    .map((id) => labelLookup.get(String(id)))
    .filter((l): l is { name: string; color: BadgeColor } => !!l);

  return (
    <>
      <div className="flex items-start gap-1.5">
        {/* Category icon in its category colour so the type reads at a glance. */}
        <span title={categoryMeta.label}>
          <CategoryIcon
            className={cn("mt-0.5 size-3.5 shrink-0", categoryMeta.iconFgClass)}
          />
        </span>
        <span className="min-w-0 flex-1 font-medium leading-snug">
          {event.title}
        </span>
        {event.recurrenceRuleId && (
          <Repeat
            className="mt-0.5 size-3 shrink-0 text-muted-foreground"
            aria-label="Recurring task"
          />
        )}
        {automations && automations.length > 0 && (
          <AutomationChip automations={automations} className="mt-0.5" />
        )}
        {member && (
          <UserAvatar
            avatarUrl={member.avatarUrl}
            firstName={member.firstName}
            lastName={member.lastName}
            email={member.email}
            className="size-5 shrink-0 text-[9px]"
            fallbackClassName="text-[9px]"
          />
        )}
      </div>

      {(labels.length > 0 || event.blocked) && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {event.blocked && (
            <Badge
              color="amber"
              variant="soft"
              className="gap-0.5 px-1 py-0 text-[10px]"
            >
              <Ban className="size-2.5" />
              Blocked
            </Badge>
          )}
          {labels.map((l) => (
            <Badge
              key={l.name}
              color={l.color}
              variant="soft"
              className="px-1 py-0 text-[10px]"
            >
              {l.name}
            </Badge>
          ))}
        </div>
      )}

      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] leading-none text-muted-foreground">
        <span
          className={cn(
            "inline-flex h-4 items-center gap-1",
            event.overdue && event.category !== "appointment"
              ? "font-medium text-red-600 dark:text-red-400"
              : "",
          )}
          title={`Due ${shortDate(event.date)}`}
        >
          <CalendarClock className="size-3 shrink-0" />
          {shortDate(event.date)}
        </span>

        {event.plannedDate && (
          <span
            className="inline-flex h-4 items-center rounded border border-dashed border-foreground/30 px-1 text-foreground/60"
            title={`Planned ${event.plannedDate}`}
          >
            Plan {shortDate(event.plannedDate)}
          </span>
        )}

        {showPriority && (
          <span className="inline-flex h-4 items-center" title={priority.label}>
            <span
              className={cn(
                "size-2 shrink-0 rounded-full",
                TASK_COLOR_DOT[priority.color],
              )}
            />
          </span>
        )}

        {event.subtaskTotal > 0 && (
          <span className="inline-flex h-4 items-center tabular-nums">
            {event.subtaskCompleted}/{event.subtaskTotal}
          </span>
        )}
      </div>

      {showJobName && event.jobName && (
        <div className="mt-1.5 flex items-center gap-1 border-t border-border/40 pt-1.5 text-[10px] text-muted-foreground">
          <Briefcase className="size-3 shrink-0" />
          <span className="truncate">{event.jobName}</span>
        </div>
      )}
    </>
  );
}
